// Lightweight className merger — no clsx/tailwind-merge dep, just space-joined truthy values.
export function cn(...args: Array<string | false | null | undefined>): string {
  return args.filter(Boolean).join(' ');
}

// formatAge renders the baby's age as a short human string.
// English: "5 days old" / "3 weeks old" / "8 months old" / "1y 6mo old"
// German:  "5 Tage alt" / "3 Wochen alt" / "8 Monate alt" / "1 J. 6 Mon. alt"
//
// We read locale inside the function (rather than receiving it as an
// arg) so every call site picks up the user's browser language without
// having to thread locale through hooks. isGerman() is a pure read of
// navigator.language; safe to call anywhere.
import { isGerman } from '@/i18n/locale';

export function formatAge(dateOfBirth: string): string {
  const dob = new Date(dateOfBirth + 'T00:00:00Z');
  const now = new Date();
  const days = Math.max(0, Math.floor((now.getTime() - dob.getTime()) / 86400000));
  const de = isGerman();

  if (days < 14) {
    if (de) return days === 1 ? '1 Tag alt' : `${days} Tage alt`;
    return `${days} day${days === 1 ? '' : 's'} old`;
  }
  if (days < 70) {
    const weeks = Math.floor(days / 7);
    if (de) return weeks === 1 ? '1 Woche alt' : `${weeks} Wochen alt`;
    return `${weeks} week${weeks === 1 ? '' : 's'} old`;
  }
  const months = Math.floor(days / 30.44);
  if (months < 24) {
    if (de) return months === 1 ? '1 Monat alt' : `${months} Monate alt`;
    return `${months} month${months === 1 ? '' : 's'} old`;
  }
  const years = Math.floor(months / 12);
  const remMonths = months - years * 12;
  if (de) {
    const yearsPart = years === 1 ? '1 Jahr' : `${years} Jahre`;
    if (remMonths === 0) return `${yearsPart} alt`;
    return `${years} J. ${remMonths} Mon. alt`;
  }
  return remMonths === 0
    ? `${years} year${years === 1 ? '' : 's'} old`
    : `${years}y ${remMonths}mo old`;
}

export function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
